import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PlusCircle, Edit3, Trash2, Globe, Lock, Eye, FileText, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  published: "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400",
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400",
};

export default function AdminPosts() {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchPosts();
  }, [filter]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API}/admin/posts${params}`, { withCredentials: true, headers });
      setPosts(res.data || []);
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/posts/${id}`, { withCredentials: true, headers });
      setPosts((prev) => prev.filter((p) => p.id !== id));
      setDeleteId(null);
      toast.success("Post deleted");
    } catch {
      toast.error("Failed to delete post");
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16 px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <div>
              <p className="overline mb-1">Admin</p>
              <h1 className="font-cormorant text-3xl font-semibold text-foreground">Manage Posts</h1>
            </div>
          </div>
          <Link
            to="/admin/editor"
            data-testid="create-post-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors"
          >
            <PlusCircle size={16} />
            New Post
          </Link>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6">
          {["all", "published", "draft"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-1.5 rounded-full font-manrope text-sm font-medium transition-colors ${
                filter === f ? "bg-brand-accent text-white" : "border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <span className="font-manrope text-sm text-muted-foreground ml-2">
            {posts.length} post{posts.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Posts Table */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-xl">
            <FileText size={40} className="text-muted-foreground mx-auto mb-4" />
            <p className="font-cormorant text-2xl text-foreground mb-2">No posts yet</p>
            <p className="font-manrope text-sm text-muted-foreground mb-6">Create your first post to get started.</p>
            <Link to="/admin/editor" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors">
              <PlusCircle size={16} /> Create Post
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.3 }}
                className="flex items-center gap-4 bg-card border border-border rounded-xl p-5 hover:border-border/80 transition-colors"
                data-testid={`post-row-${post.id}`}
              >
                {/* Status indicator */}
                <div className={`w-2 h-2 rounded-full shrink-0 ${
                  post.status === "published" ? "bg-green-500" : "bg-muted-foreground"
                }`} />

                {/* Post info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-manrope text-sm font-semibold text-foreground truncate">{post.title}</h3>
                    <span className={`font-manrope text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[post.status] || STATUS_STYLES.draft}`}>
                      {post.status}
                    </span>
                    {post.visibility === "paid" && (
                      <span className="flex items-center gap-1 font-manrope text-xs px-2 py-0.5 rounded-full bg-accent/10 text-brand-accent">
                        <Lock size={10} /> Premium
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="font-manrope text-xs text-muted-foreground">
                      {post.status === "published" ? `Published ${formatDate(post.published_at)}` : `Created ${formatDate(post.created_at)}`}
                    </span>
                    {post.tags && post.tags.length > 0 && (
                      <span className="font-manrope text-xs text-muted-foreground">
                        {post.tags.slice(0, 2).join(", ")}
                      </span>
                    )}
                    <span className="font-manrope text-xs text-muted-foreground flex items-center gap-1">
                      <Eye size={11} /> {post.views || 0} views
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  {post.status === "published" && (
                    <Link
                      to={`/post/${post.slug}`}
                      target="_blank"
                      className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="View post"
                    >
                      <Globe size={16} />
                    </Link>
                  )}
                  <Link
                    to={`/admin/editor/${post.id}`}
                    data-testid={`edit-post-${post.id}`}
                    className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title="Edit post"
                  >
                    <Edit3 size={16} />
                  </Link>
                  {deleteId === post.id ? (
                    <div className="flex items-center gap-1.5 ml-2">
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="px-2.5 py-1 rounded-md bg-destructive text-destructive-foreground font-manrope text-xs hover:opacity-90 transition-opacity"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeleteId(null)}
                        className="px-2.5 py-1 rounded-md border border-border font-manrope text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteId(post.id)}
                      data-testid={`delete-post-${post.id}`}
                      className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
                      title="Delete post"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
